import {
  createAnalyticsEvent,
  getAdobeAnalytics,
  getCachedPageData,
  getComponentViewsAndClear as getComponentViewsAndClearUtil,
  getMergedSearchContext,
  handlePageExit,
  setCachedPageData,
  setupPageExitListeners,
  trackComponentView as trackComponentViewUtil,
} from "./analyticsUtils";
import { AnalyticsSdk } from "./initLogger";
import {
  AnalyticsEventObject,
  ComponentViewInput,
  ComponentViewResult,
  ManualEventData,
  PageInitializationData,
  SearchContextParams,
} from "./types";

/**
 * Push event to Adobe Data Layer
 * @param eventObj Event object to push
 */
export const pushToSdkEvent = (
  eventObj: AnalyticsEventObject | Record<string, unknown>
) => {
  try {
    const extraOptions = {
      lob: "travelinsurance",
      destinationUrl: window.location.href,
    };
    AnalyticsSdk.pushToAdobeDataLayer(eventObj, extraOptions);
  } catch (error) {
    console.error("Error pushing event to Adobe Data Layer", error);
  }
};

/**
 * Initialize page tracking - call during React component mount
 * Caches page-level data and triggers automatic 'page-render' event
 */
export const initializePageTracking = (
  pageData: PageInitializationData,
  searchContext?: SearchContextParams
): void => {
  // Store page data in memory for current session
  setCachedPageData(pageData);

  // Use the imported functions from analyticsUtils
  let mergedSearchContext = getMergedSearchContext(searchContext);
  if (searchContext?.isReadStoreSearchContext) {
    mergedSearchContext = JSON.parse(
      sessionStorage.getItem("STORE_SEARCH_CONTEXT") || "{}"
    );
  }
  const adobeAnalytics = getAdobeAnalytics(
    mergedSearchContext,
    pageData.event_detail?.components?.content_details || []
  );

  // Set up page exit listeners now that we have cached data
  setupPageExitListeners();

  if (searchContext?.isStoreSearchContext) {
    sessionStorage.setItem(
      "STORE_SEARCH_CONTEXT",
      JSON.stringify(mergedSearchContext)
    );
  }

  // Automatically trigger 'page-render' event
  const pageRenderEvent = createAnalyticsEvent(pageData, {
    event_detail: {
      event_value: "page_load",
      event_name: "page-rendered",
      event_type: "page",
    },
    search_context: mergedSearchContext,
    adobe_analytics: adobeAnalytics,
  });
  pushToSdkEvent(pageRenderEvent);
};

/**
 * Initialize page tracking - call during React component mount
 * Caches page-level data and triggers automatic 'page-render' event
 */
export const initializeErrorPageTracking = (
  pageData: PageInitializationData,
  searchContext?: SearchContextParams
): void => {
  // Store page data in memory for current session
  setCachedPageData(pageData);

  // Use the imported functions from analyticsUtils
  let mergedSearchContext = getMergedSearchContext(searchContext);
  if (searchContext?.isReadStoreSearchContext) {
    mergedSearchContext = JSON.parse(
      sessionStorage.getItem("STORE_SEARCH_CONTEXT") || "{}"
    );
  }
  const adobeAnalytics = getAdobeAnalytics(
    mergedSearchContext,
    pageData.event_detail?.components?.content_details || [],
    pageData.error_details_list || []
  );

  // Set up page exit listeners now that we have cached data
  setupPageExitListeners();

  if (searchContext?.isStoreSearchContext) {
    sessionStorage.setItem(
      "STORE_SEARCH_CONTEXT",
      JSON.stringify(mergedSearchContext)
    );
  }

  // Automatically trigger 'page-render' event
  const pageRenderEvent = createAnalyticsEvent(pageData, {
    event_detail: {
      event_value: "error",
      event_name: "error",
      event_type: "action",
    },
    search_context: mergedSearchContext,
    adobe_analytics: adobeAnalytics,
  });
  pushToSdkEvent(pageRenderEvent);
};

/**
 * Track manual events - call on-demand for user interactions
 * Merges event-specific data with cached page data and search context
 */
export const trackEvent = (
  eventData: ManualEventData,
  searchContext?: SearchContextParams
): void => {
  const cachedPageData = getCachedPageData();

  if (!cachedPageData) {
    console.warn(
      "Page tracking not initialized. Call initializePageTracking first."
    );
    return;
  }

  // Get merged search context using utility function
  const mergedSearchContext = getMergedSearchContext(searchContext);
  const adobeAnalytics = getAdobeAnalytics(
    mergedSearchContext,
    eventData?.event_detail?.components?.content_details
  );

  // Create event data with merged search context
  const eventDataWithSearchContext: ManualEventData = {
    ...eventData,
    search_context: mergedSearchContext,
    adobe_analytics: adobeAnalytics,
  };

  const analyticsEvent = createAnalyticsEvent(
    cachedPageData,
    eventDataWithSearchContext
  );
  pushToSdkEvent(analyticsEvent);
};

/**
 * Track manual events - call on-demand for user interactions
 * Merges event-specific data with cached page data and search context
 */
export const errorTrackEvent = (
  eventData: ManualEventData,
  searchContext?: SearchContextParams
): void => {
  const cachedPageData = getCachedPageData();

  if (!cachedPageData) {
    console.warn(
      "Page tracking not initialized. Call initializePageTracking first."
    );
    return;
  }

  // Get merged search context using utility function
  const mergedSearchContext = getMergedSearchContext(searchContext);
  const adobeAnalytics = getAdobeAnalytics(
    mergedSearchContext,
    eventData?.event_detail?.components?.content_details,
    eventData?.error_details_list
  );

  // Create event data with merged search context
  const eventDataWithSearchContext: ManualEventData = {
    ...eventData,
    event_detail: {
      event_value: "error",
      event_name: "error",
      event_type: "action",
    },
    search_context: mergedSearchContext,
    adobe_analytics: adobeAnalytics,
  };

  const analyticsEvent = createAnalyticsEvent(
    cachedPageData,
    eventDataWithSearchContext
  );
  pushToSdkEvent(analyticsEvent);
};

/**
 * Manually trigger page exit event - call during component unmount
 * This is useful for React component cleanup or manual exit tracking
 */
export const triggerPageExit = (): void => {
  handlePageExit();
};
/**
 * Update search context - call to update the search context
 * @param searchContext Search context to update
 */
export const updateSearchContext = (
  searchContext: SearchContextParams
): void => {
  getMergedSearchContext(searchContext);
};

/**
 * Track component view - call when a component is viewed
 * @param input Component view data to track
 */
export const trackComponentView = (input: ComponentViewInput): void => {
  trackComponentViewUtil(input);
};

/**
 * Get all tracked component views and clear cache
 * @returns ComponentViewResult with all tracked components
 */
export const getComponentViewsAndClear = (): ComponentViewResult => {
  return getComponentViewsAndClearUtil();
};
